import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  MessageSquare, 
  ExternalLink,
  Clock,
  Loader2,
  Send,
  Trash2
} from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

interface GiftRankingProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; tab?: string }) => void;
  giftGroupId: string | null;
  embedded?: boolean; // When true, hides the outer wrapper and header
}

interface Gift {
  id: number;
  name: string;
  description: string;
  price: number;
  orderByDate: string;
  yourRank: number | null;
  groupRank: number | null;
  averageRank: number | null;
  rankedBy: number;
  commentCount: number;
  url: string;
  imageUrl?: string;
  source: string;
  suggestedBy?: number;
}

interface Comment {
  id: number;
  userId: number;
  userName: string;
  content: string;
  createdAt: string;
}

export function GiftRanking({ onNavigate, giftGroupId, embedded = false }: GiftRankingProps) {
  const [giftPool, setGiftPool] = useState(0);
  const [targetPool, setTargetPool] = useState(0);
  const [showComments, setShowComments] = useState<number | null>(null);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [comments, setComments] = useState<Record<number, Comment[]>>({});
  const [newComment, setNewComment] = useState<Record<number, string>>({});
  const [postingComment, setPostingComment] = useState<number | null>(null);

  useEffect(() => {
    if (giftGroupId) {
      loadSuggestions();
      loadGiftGroup();
      loadCurrentUser();
    }
  }, [giftGroupId]);

  const loadCurrentUser = async () => {
    try {
      const user = await api.getCurrentUser();
      const userId = Number(user.id);
      setCurrentUserId(userId);
      console.log('Current user ID loaded:', userId);
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  };

  const loadGiftGroup = async () => {
    if (!giftGroupId) return;
    
    try {
      const group = await api.getGiftGroup(giftGroupId);
      setGiftPool(group.total_pool || 0);
      
      // Calculate target pool
      const acceptedGifters = group.members.filter((m: any) => m.role === 'gifter' && m.status === 'accepted');
      const target = (group.recommended_contribution || 0) * acceptedGifters.length;
      setTargetPool(target);
    } catch (error) {
      console.error('Failed to load gift group:', error);
    }
  };

  const loadSuggestions = async () => {
    if (!giftGroupId) return;
    
    try {
      setLoading(true);
      const response = await api.getGiftSuggestions(giftGroupId);
      
      // Transform API data to component format
      const transformedGifts: Gift[] = response.suggestions.map((s: any) => {
        const gift = {
          id: s.id,
          name: s.name,
          description: s.description,
          price: s.price,
          orderByDate: s.orderByDate,
          yourRank: s.yourRank,
          groupRank: s.groupRank,
          averageRank: s.averageRank,
          rankedBy: s.rankedBy,
          commentCount: s.commentCount,
          url: s.url,
          imageUrl: s.imageUrl,
          source: s.source,
          suggestedBy: s.suggestedBy ? Number(s.suggestedBy) : undefined
        };
        console.log('Gift loaded:', gift.name, 'suggestedBy:', gift.suggestedBy);
        return gift;
      });

      setGifts(transformedGifts);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
      toast.error('Failed to load gift suggestions');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async (suggestionId: number) => {
    if (!giftGroupId) return;

    try {
      const response = await api.getComments(giftGroupId, suggestionId.toString());
      setComments(prev => ({
        ...prev,
        [suggestionId]: response.comments
      }));
    } catch (error) {
      console.error('Failed to load comments:', error);
      toast.error('Failed to load comments');
    }
  };

  const handleDeleteSuggestion = async (suggestionId: number) => {
    if (!giftGroupId) return;

    try {
      await api.deleteGiftSuggestion(giftGroupId, suggestionId.toString());
      setGifts(prevGifts => prevGifts.filter(g => g.id !== suggestionId));
      toast.success('Suggestion deleted');
    } catch (error) {
      console.error('Failed to delete suggestion:', error);
      toast.error('Failed to delete suggestion');
    }
  };

  const updateRanking = async (giftId: number, newRank: number | null) => {
    if (!giftGroupId) return;

    // Capture the updated state to use for API call
    let updatedGiftsState: typeof gifts = [];

    setGifts(prevGifts => {
      const updatedGifts = [...prevGifts];
      const giftIndex = updatedGifts.findIndex(g => g.id === giftId);
      const oldRank = updatedGifts[giftIndex].yourRank;
      
      // Update this gift's rank
      updatedGifts[giftIndex].yourRank = newRank;
      
      // If assigning a rank (not unranking), bump others that are in the way
      if (newRank !== null) {
        updatedGifts.forEach((gift, index) => {
          // Skip the gift we're ranking
          if (index === giftIndex) return;
          
          // Skip unranked gifts
          if (gift.yourRank === null) return;
          
          if (oldRank === null) {
            // Inserting a new rank: bump down gifts at or after this rank
            if (gift.yourRank >= newRank) {
              gift.yourRank += 1;
            }
          } else {
            // Moving an existing rank
            if (newRank < oldRank) {
              // Moving up: bump down gifts in between
              if (gift.yourRank >= newRank && gift.yourRank < oldRank) {
                gift.yourRank += 1;
              }
            } else {
              // Moving down: pull up gifts in between
              if (gift.yourRank <= newRank && gift.yourRank > oldRank) {
                gift.yourRank -= 1;
              }
            }
          }
        });
      }
      
      // Save updated state for API call
      updatedGiftsState = updatedGifts;
      return updatedGifts;
    });

    // Save rankings to backend (use captured updated state)
    try {
      setSaving(true);
      // Only send ranked gifts (rank > 0), not unranked items
      const rankings = updatedGiftsState
        .filter(g => g.yourRank !== null && g.yourRank > 0)
        .map(g => ({
          suggestionId: g.id,
          rank: g.yourRank as number
        }));

      const response = await api.updateRankings(giftGroupId, rankings);
      
      // Update group rankings from response and resort
      setGifts(prevGifts => {
        const updated = [...prevGifts];
        
        // Update all gifts
        updated.forEach(gift => {
          const ranking = response.updatedGroupRankings.find((r: any) => r.suggestionId === gift.id);
          if (ranking) {
            // Gift has rankings - update from response
            gift.groupRank = ranking.groupRank;
            gift.averageRank = ranking.averageRank;
            gift.rankedBy = ranking.rankedBy || 0;
          } else {
            // Gift has no rankings - clear group rank
            gift.groupRank = null;
            gift.averageRank = null;
            gift.rankedBy = 0;
          }
        });
        
        // Sort by group rank for better UX
        return updated.sort((a, b) => {
          // Ranked items come first, sorted by group rank (ascending)
          if (a.groupRank !== null && b.groupRank !== null) {
            return a.groupRank - b.groupRank;
          }
          // Ranked items before unranked
          if (a.groupRank !== null) return -1;
          if (b.groupRank !== null) return 1;
          // Both unranked - sort by name
          return a.name.localeCompare(b.name);
        });
      });

      toast.success('Ranking saved');
    } catch (error) {
      console.error('Failed to save rankings:', error);
      toast.error('Failed to save rankings');
      // Reload to get correct state
      loadSuggestions();
    } finally {
      setSaving(false);
    }
  };

  const toggleComments = async (giftId: number) => {
    if (showComments === giftId) {
      setShowComments(null);
    } else {
      setShowComments(giftId);
      if (!comments[giftId]) {
        await loadComments(giftId);
      }
    }
  };

  const handlePostComment = async (suggestionId: number) => {
    if (!giftGroupId || !newComment[suggestionId]?.trim()) return;

    try {
      setPostingComment(suggestionId);
      const response = await api.addComment(
        giftGroupId,
        newComment[suggestionId],
        suggestionId
      );

      // Add comment to local state
      setComments(prev => ({
        ...prev,
        [suggestionId]: [...(prev[suggestionId] || []), response.comment]
      }));

      // Update comment count
      setGifts(prevGifts => {
        const updated = [...prevGifts];
        const gift = updated.find(g => g.id === suggestionId);
        if (gift) {
          gift.commentCount += 1;
        }
        return updated;
      });

      // Clear input
      setNewComment(prev => ({ ...prev, [suggestionId]: '' }));
      toast.success('Comment added');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setPostingComment(null);
    }
  };

  // Determine which gifts will be purchased based on group rank and cumulative cost
  const getGiftsThatWillBePurchased = () => {
    const rankedGifts = gifts
      .filter(g => g.groupRank !== null)
      .sort((a, b) => (a.groupRank || 0) - (b.groupRank || 0));
    
    const willBePurchased = new Set<number>();
    let remainingPool = giftPool;
    
    for (const gift of rankedGifts) {
      if (gift.price <= remainingPool) {
        willBePurchased.add(gift.id);
        remainingPool -= gift.price;
      } else {
        break; // Can't afford this or any lower-ranked gifts
      }
    }
    
    return willBePurchased;
  };

  const getWillBePurchased = (gift: Gift) => {
    const purchaseSet = getGiftsThatWillBePurchased();
    return purchaseSet.has(gift.id);
  };

  const getPriceStatus = (gift: Gift) => {
    const willBePurchased = getWillBePurchased(gift);
    
    if (willBePurchased) {
      return {
        variant: 'default' as const,
        text: 'Purchasing',
        color: 'text-green-600',
        badgeClass: 'bg-green-600 text-white border-green-600'
      };
    }
    
    if (gift.price > targetPool) {
      return {
        variant: 'destructive' as const,
        text: 'Over Pool Target',
        color: 'text-red-600',
        badgeClass: 'border-red-600 text-red-600'
      };
    }
    
    if (gift.price > giftPool) {
      return {
        variant: 'outline' as const,
        text: 'Over Pool Status',
        color: 'text-yellow-600',
        badgeClass: 'border-yellow-600 text-yellow-600'
      };
    }
    
    return {
      variant: 'outline' as const,
      text: 'Within Price Range',
      color: 'text-blue-600',
      badgeClass: 'border-blue-600 text-blue-600'
    };
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } catch {
      return dateString;
    }
  };

  if (!giftGroupId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="p-6 text-center">
          <h2 className="text-xl mb-2">No Gift Group Selected</h2>
          <p className="text-muted-foreground mb-4">Please select a gift group first</p>
          <Button onClick={() => onNavigate('dashboard', { tab: 'todo' })}>Go to Dashboard</Button>
        </Card>
      </div>
    );
  }

  const content = (
    <>
      {/* Header - only show if not embedded */}
      {!embedded && (
        <Card className="p-6 border-2 border-primary/20 mb-6 shadow-lg">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-primary mb-2">Gift Ranking</h1>
              <h2 className="text-xl text-muted-foreground">Rank gifts by preference</h2>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Gift Pool Balance</div>
              <div className="text-3xl text-green-600">${giftPool.toFixed(2)}</div>
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="p-12 text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-primary animate-spin" />
          <p className="text-muted-foreground">Loading gift suggestions...</p>
        </div>
      ) : gifts.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">No gift suggestions yet</p>
          <Button onClick={() => onNavigate('gift-suggestions', { giftGroupId })}>
            Add Gift Suggestions
          </Button>
        </Card>
      ) : (
        <>
          {/* Ranking Table */}
          <Card className="border-2 border-primary/20 shadow-lg mb-6">
            <div className="p-6 border-b-2 border-primary/20 flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-xl text-primary">Collaborative Gift Ranking</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Rank gifts by preference. Changes save automatically. Group ranking determines which gifts get purchased.
                  {saving && <span className="ml-2 text-blue-600">💾 Saving...</span>}
                </p>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Gift Pool Balance</div>
                <div className="text-2xl text-green-600">${giftPool.toFixed(2)}</div>
              </div>
            </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Your Rank</TableHead>
                      <TableHead className="w-28">Group Rank</TableHead>
                      <TableHead className="min-w-[250px] max-w-[400px]">Gift Name</TableHead>
                      <TableHead className="w-24">Cost</TableHead>
                      <TableHead className="w-24">Source</TableHead>
                      <TableHead className="w-32">Order By Date</TableHead>
                      <TableHead className="w-20">Comments</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Sort gifts by Group Rank for display */}
                    {[...gifts]
                      .sort((a, b) => {
                        // Ranked items come first, sorted by group rank (ascending)
                        if (a.groupRank !== null && b.groupRank !== null) {
                          return a.groupRank - b.groupRank;
                        }
                        // Ranked items before unranked
                        if (a.groupRank !== null) return -1;
                        if (b.groupRank !== null) return 1;
                        // Both unranked - sort by name
                        return a.name.localeCompare(b.name);
                      })
                      .map((gift) => (
                      <>
                        <TableRow 
                          key={gift.id}
                          className={getWillBePurchased(gift) ? 'bg-green-50 border-l-4 border-green-500' : ''}
                        >
                          <TableCell>
                            <Select
                              value={gift.yourRank?.toString() || 'unranked'}
                              onValueChange={(value) => updateRanking(gift.id, value === 'unranked' ? null : parseInt(value))}
                            >
                              <SelectTrigger className="w-20">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unranked">-</SelectItem>
                                {Array.from({ length: 10 }, (_, i) => i + 1).map(rank => (
                                  <SelectItem key={rank} value={rank.toString()}>
                                    {rank}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-base">
                                {gift.groupRank || '-'}
                              </Badge>
                              {getWillBePurchased(gift) && (
                                <Badge className="bg-green-600 text-xs whitespace-nowrap">
                                  Will Purchase
                                </Badge>
                              )}
                            </div>
                            {gift.rankedBy > 0 && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {gift.rankedBy} ranked
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="min-w-[250px] max-w-[400px]">
                            <div className="w-full">
                              <div className="break-words whitespace-normal hyphens-auto">{gift.name}</div>
                              <div className="text-xs text-muted-foreground break-words whitespace-normal mt-1">{gift.description}</div>
                              <div className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                <a href={gift.url} target="_blank" rel="noopener noreferrer" className="hover:underline break-all truncate">
                                  View Product
                                </a>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const status = getPriceStatus(gift);
                              return (
                                <>
                                  <Badge 
                                    variant="outline"
                                    className={`text-sm ${status.badgeClass}`}
                                  >
                                    ${gift.price.toFixed(2)}
                                  </Badge>
                                  <div className={`text-xs mt-1 ${status.color}`}>
                                    {status.text}
                                  </div>
                                </>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="default" className="bg-green-600">
                              {gift.source === 'ai' ? 'AI' : 'Manual'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{formatDate(gift.orderByDate)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleComments(gift.id)}
                              className="relative"
                            >
                              <MessageSquare className="w-4 h-4" />
                              {gift.commentCount > 0 && (
                                <Badge 
                                  variant="destructive" 
                                  className="absolute -top-1 -right-1 h-4 w-4 p-0 text-xs flex items-center justify-center"
                                >
                                  {gift.commentCount}
                                </Badge>
                              )}
                            </Button>
                          </TableCell>
                          <TableCell>
                            {/* Always show delete button for debugging */}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                console.log('Delete clicked for gift:', gift.id);
                                console.log('gift.suggestedBy:', gift.suggestedBy, 'type:', typeof gift.suggestedBy);
                                console.log('currentUserId:', currentUserId, 'type:', typeof currentUserId);
                                console.log('Match:', Number(gift.suggestedBy) === Number(currentUserId));
                                handleDeleteSuggestion(gift.id);
                              }}
                              title={`Delete (Debug: suggestedBy=${gift.suggestedBy}, currentUser=${currentUserId})`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        
                        {showComments === gift.id && (
                          <TableRow>
                            <TableCell colSpan={8} className="bg-muted/30">
                              <Card className="p-4 border-2 border-primary/20">
                                <h4 className="mb-3 flex items-center gap-2 text-primary">
                                  <MessageSquare className="w-4 h-4" />
                                  Comments & Discussion
                                </h4>
                                
                                {comments[gift.id] && comments[gift.id].length > 0 && (
                                  <div className="space-y-3 mb-4">
                                    {comments[gift.id].map((comment) => (
                                      <Card key={comment.id} className="p-3 bg-background border">
                                        <div className="flex items-start gap-2">
                                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm text-primary flex-shrink-0">
                                            {comment.userName.slice(0, 2).toUpperCase()}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm mb-1">
                                              <strong>{comment.userName}</strong>
                                              <span className="text-muted-foreground text-xs ml-2">
                                                {formatTimeAgo(comment.createdAt)}
                                              </span>
                                            </div>
                                            <p className="text-sm text-muted-foreground break-words">
                                              {comment.content}
                                            </p>
                                          </div>
                                        </div>
                                      </Card>
                                    ))}
                                  </div>
                                )}
                                
                                <div className="flex gap-2">
                                  <Textarea 
                                    placeholder="Add a comment..."
                                    className="flex-1"
                                    rows={2}
                                    value={newComment[gift.id] || ''}
                                    onChange={(e) => setNewComment(prev => ({
                                      ...prev,
                                      [gift.id]: e.target.value
                                    }))}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handlePostComment(gift.id);
                                      }
                                    }}
                                  />
                                  <Button 
                                    onClick={() => handlePostComment(gift.id)}
                                    disabled={!newComment[gift.id]?.trim() || postingComment === gift.id}
                                    className="self-end"
                                  >
                                    {postingComment === gift.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Send className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              </Card>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>

          {/* Navigation Buttons - only show if not embedded */}
          {!embedded && (
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  onClick={() => onNavigate('gift-group-overview', { giftGroupId })}
                >
                  Back to Overview
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => onNavigate('ai-gift-suggestions', { giftGroupId })}
                >
                  AI Suggestions
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => onNavigate('gift-suggestions', { giftGroupId })}
                >
                  Manual Suggestions
                </Button>
              </div>
              
              <Button
                onClick={() => {
                  console.log('🎯 GiftRanking: Done - Return to Dashboard clicked');
                  console.log('🎯 Calling onNavigate with:', { screen: 'dashboard', data: { tab: 'todo' } });
                  onNavigate('dashboard', { tab: 'todo' });
                }}
                className="bg-primary hover:bg-primary/90"
              >
                Done - Return to Dashboard
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );

  // If embedded, return just the content
  if (embedded) {
    return content;
  }

  // Otherwise wrap in the full page layout
  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-7xl mx-auto">
        {content}
      </div>
    </div>
  );
}